import { JourneyDraft } from './draft';

// What the editor shows on the right; keys point into the draft's miles, steps and blocks.
export type Selection =
  | { kind: 'info' }
  | { kind: 'journeyComplete' }
  | { kind: 'mile'; mileKey: string }
  | { kind: 'mileComplete'; mileKey: string }
  | { kind: 'step'; mileKey: string; stepKey: string }
  | { kind: 'block'; mileKey: string; stepKey: string; blockKey: string };

export const INFO_SELECTION: Selection = { kind: 'info' };

export type DeleteTarget = Extract<Selection, { kind: 'mile' | 'step' | 'block' }>;

// Deleting the node that holds the selection moves it up to the node's parent.
export const selectionAfterDelete = (current: Selection, target: DeleteTarget): Selection => {
  if (target.kind === 'mile') {
    return 'mileKey' in current && current.mileKey === target.mileKey ? INFO_SELECTION : current;
  }
  if (target.kind === 'step') {
    return 'stepKey' in current && current.stepKey === target.stepKey
      ? { kind: 'mile', mileKey: target.mileKey }
      : current;
  }

  return 'blockKey' in current && current.blockKey === target.blockKey
    ? { kind: 'step', mileKey: target.mileKey, stepKey: target.stepKey }
    : current;
};

export interface Location {
  mileIndex: number;
  stepIndex: number;
  blockIndex: number;
}

// Finds the selected node in the draft; -1 marks a level the selection doesn't reach, and null a
// node that is gone (deleted, or never saved).
export const locate = (draft: JourneyDraft, selection: Selection): Location | null => {
  if (selection.kind === 'info' || selection.kind === 'journeyComplete') {
    return { mileIndex: -1, stepIndex: -1, blockIndex: -1 };
  }

  const mileIndex = draft.miles.findIndex((mile) => mile.key === selection.mileKey);
  if (mileIndex === -1) return null;
  if (selection.kind === 'mile' || selection.kind === 'mileComplete') {
    return { mileIndex, stepIndex: -1, blockIndex: -1 };
  }

  const steps = draft.miles[mileIndex].steps;
  const stepIndex = steps.findIndex((step) => step.key === selection.stepKey);
  if (stepIndex === -1) return null;
  if (selection.kind === 'step') {
    return { mileIndex, stepIndex, blockIndex: -1 };
  }

  const blockIndex = steps[stepIndex].blocks.findIndex((block) => block.key === selection.blockKey);
  return blockIndex === -1 ? null : { mileIndex, stepIndex, blockIndex };
};

// A save gives new nodes their ids, and so new keys; the order is kept, so follow the selection by
// position into the saved draft.
export const reselect = (
  selection: Selection,
  before: JourneyDraft,
  after: JourneyDraft,
): Selection => {
  const location = locate(before, selection);
  if (!location) return INFO_SELECTION;
  if (selection.kind === 'info' || selection.kind === 'journeyComplete') return selection;

  const mile = after.miles[location.mileIndex];
  if (!mile) return INFO_SELECTION;
  if (selection.kind === 'mile' || selection.kind === 'mileComplete') {
    return { kind: selection.kind, mileKey: mile.key };
  }

  const step = mile.steps[location.stepIndex];
  if (!step) return { kind: 'mile', mileKey: mile.key };
  if (selection.kind === 'step') {
    return { kind: 'step', mileKey: mile.key, stepKey: step.key };
  }

  const block = step.blocks[location.blockIndex];
  return block
    ? { kind: 'block', mileKey: mile.key, stepKey: step.key, blockKey: block.key }
    : { kind: 'step', mileKey: mile.key, stepKey: step.key };
};
